import * as React from "react";

import type { VariantProps } from "class-variance-authority";

import { cva } from "class-variance-authority";

import { cn } from "../../utils";
import { Button } from "./button";
import { Input } from "./input";
import { Textarea } from "./textarea";

const InputGroup = ({ className, ...props }: React.ComponentProps<"div">) => {
  return (
    <div
      data-slot="input-group"
      role="group"
      className={cn(
        "af-group/input-group af-shadow-xs af-relative af-flex af-w-full af-items-center af-rounded-md af-border af-border-input af-outline-none af-transition-[color,box-shadow] dark:af-bg-input/30",
        "af-h-10 has-[>textarea]:af-h-auto",

        // Variants based on alignment.
        "has-[>[data-align=inline-start]]:[&>input]:af-pl-2",
        "has-[>[data-align=inline-end]]:[&>input]:af-pr-2",
        "has-[>[data-align=block-start]]:af-h-auto has-[>[data-align=block-start]]:af-flex-col has-[>[data-align=block-start]]:[&>input]:af-pb-3",
        "has-[>[data-align=block-end]]:af-h-auto has-[>[data-align=block-end]]:af-flex-col has-[>[data-align=block-end]]:[&>input]:af-pt-3",

        // Focus state.
        "has-[[data-slot=input-group-control]:focus-visible]:af-ring-2 has-[[data-slot=input-group-control]:focus-visible]:af-ring-ring has-[[data-slot=input-group-control]:focus-visible]:af-ring-offset-2",

        // Error state.
        "has-[[data-slot][aria-invalid=true]]:af-border-destructive has-[[data-slot][aria-invalid=true]]:af-ring-destructive/20 dark:has-[[data-slot][aria-invalid=true]]:af-ring-destructive/40",

        className,
      )}
      {...props}
    />
  );
};
InputGroup.displayName = "InputGroup";

const inputGroupAddonVariants = cva(
  "af-group-data-[disabled=true]/input-group:opacity-50 af-flex af-h-auto af-cursor-text af-select-none af-items-center af-justify-center af-gap-2 af-py-1.5 af-text-sm af-font-medium af-text-muted-foreground [&>svg:not([class*=size-])]:af-size-4",
  {
    variants: {
      align: {
        "inline-start":
          "af-order-first af-pl-3 has-[>button]:af-ml-[-0.45rem] has-[>kbd]:af-ml-[-0.35rem]",
        "inline-end":
          "af-order-last af-pr-3 has-[>button]:af-mr-[-0.4rem] has-[>kbd]:af-mr-[-0.35rem]",
        "block-start":
          "[.border-b]:af-pb-3 af-group-has-[>input]/input-group:pt-2.5 af-order-first af-w-full af-justify-start af-px-3 af-pt-3",
        "block-end":
          "[.border-t]:af-pt-3 af-group-has-[>input]/input-group:pb-2.5 af-order-last af-w-full af-justify-start af-px-3 af-pb-3",
      },
    },
    defaultVariants: {
      align: "inline-start",
    },
  },
);

const InputGroupAddon = ({
  className,
  align = "inline-start",
  ...props
}: React.ComponentProps<"div"> &
  VariantProps<typeof inputGroupAddonVariants>) => {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("button")) {
          return;
        }
        e.currentTarget.parentElement?.querySelector("input")?.focus();
      }}
      {...props}
    />
  );
};
InputGroupAddon.displayName = "InputGroupAddon";

const inputGroupButtonVariants = cva(
  "af-flex af-items-center af-gap-2 af-text-sm af-shadow-none",
  {
    variants: {
      size: {
        xs: "af-h-6 af-gap-1 af-rounded-[calc(var(--radius)-5px)] af-px-2 has-[>svg]:af-px-2 [&>svg:not([class*=size-])]:af-size-3.5",
        sm: "af-h-8 af-gap-1.5 af-rounded-md af-px-2.5 has-[>svg]:af-px-2.5",
        "icon-xs":
          "af-size-6 af-rounded-[calc(var(--radius)-5px)] af-p-0 has-[>svg]:af-p-0",
        "icon-sm": "af-size-8 af-p-0 has-[>svg]:af-p-0",
      },
    },
    defaultVariants: {
      size: "xs",
    },
  },
);

const InputGroupButton = ({
  className,
  type = "button",
  variant = "ghost",
  size = "xs",
  ...props
}: Omit<React.ComponentProps<typeof Button>, "size"> &
  VariantProps<typeof inputGroupButtonVariants>) => {
  return (
    <Button
      type={type}
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  );
};
InputGroupButton.displayName = "InputGroupButton";

const InputGroupText = ({
  className,
  ...props
}: React.ComponentProps<"span">) => {
  return (
    <span
      className={cn(
        "af-flex af-items-center af-gap-2 af-text-sm af-text-muted-foreground [&_svg:not([class*=size-])]:af-size-4 [&_svg]:af-pointer-events-none",
        className,
      )}
      {...props}
    />
  );
};
InputGroupText.displayName = "InputGroupText";

const InputGroupInput = ({
  className,
  ...props
}: React.ComponentProps<"input">) => {
  return (
    <Input
      data-slot="input-group-control"
      className={cn(
        "af-flex-1 af-rounded-none af-border-0 af-bg-transparent af-shadow-none focus-visible:af-ring-0 focus-visible:af-ring-offset-0 dark:af-bg-transparent",
        className,
      )}
      {...props}
    />
  );
};
InputGroupInput.displayName = "InputGroupInput";

const InputGroupTextarea = ({
  className,
  ...props
}: React.ComponentProps<"textarea">) => {
  return (
    <Textarea
      data-slot="input-group-control"
      className={cn(
        "af-rounded-inherit af-flex-1 af-resize-none af-border-0 af-bg-transparent af-py-3 af-shadow-none focus-visible:af-ring-0 focus-visible:af-ring-offset-0 dark:af-bg-transparent",
        className,
      )}
      {...props}
    />
  );
};
InputGroupTextarea.displayName = "InputGroupTextarea";

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupInput,
  InputGroupTextarea,
};
